import type {
  ShiftRecord,
  ApiErrorResponseData
} from '@libs/data-access/queries'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import { usePadSettings } from '@spa/scenes/Plannings/hooks'
import color from 'color'
import Immutable from 'immutable'
import moment from 'moment'
import { useState } from 'react'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { getContractById } from 'snap-redux/modules/contracts/selectors'
import { getLabelsByTeamId } from 'snap-redux/modules/labels/selectors'
import { getLocationById } from 'snap-redux/modules/locations/selectors'
import { useRoleRights } from 'snap-redux/modules/memberships/utils'
import { deleteShift } from 'snap-redux/modules/shifts'
import { getTeamById } from 'snap-redux/modules/teams/selectors'
import { useActions } from 'snap-redux/utils'

import { getFullDate } from '../../RestForm/RestFormSection/utils/formatDate'
import { NO_LABEL_VALUE } from '../../ShiftForm/utils/getLabelFormatted'
import { computeRealShiftDates } from '../../utils'
import type { CommonFormRealDataLegacy } from '../models/CommonFormRealData'

import useRealFormValidationPermissionLegacy from './useRealFormValidationPermissionLegacy'

const actionCreators = {
  deleteShift
}

/**
 * @deprecated file
 */

export const useRealFormInitLegacy = ({
  shiftData,
  formSuccess,
  currentUserId,
  precomputedCanValidate
}: {
  shiftData?: ShiftRecord
  formSuccess: () => void
  currentUserId: number
  precomputedCanValidate?: boolean
}) => {
  const { t } = usePlanningFormTranslation()
  const actions = useActions(actionCreators)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<ApiErrorResponseData>()
  const noLabel = { id: '', name: t('realHoursForm.nolabel'), color: '#f8f9fb' }

  const shift =
    useMemoSelector((state: ReduxState) =>
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      state.shifts.getIn(['data', shiftData.id])
    ) || shiftData

  // FROM PLANNED

  const account = useMemoSelector(getCurrentAccount)

  const associatRestIdsSet = useMemoSelector(
    (state: ReduxState) =>
      shift && state.rests.getIn(['relations', 'original_shift_id', shift.id])
  )
  const associatRestId = associatRestIdsSet && associatRestIdsSet.first()
  const rest = useMemoSelector(
    (state: ReduxState) =>
      associatRestId && state.rests.getIn(['data', associatRestId])
  )

  const [textFields, setTextField] = useState({
    shift: {
      real_note: shift?.real_note || ''
    },
    rest: {
      note: rest?.note || ''
    }
  })

  // ----------------

  // FROM UNPLANNED

  const { location_id: contractLocationId } = useMemoSelector(
    (state: ReduxState) => getContractById(state, shift.user_contract_id)
  )
  const { atLeast } = useRoleRights()
  const shiftWeeklyscheduleId = shift.weeklyschedule_id

  const shiftWeeklyschedule = useMemoSelector((state: ReduxState) =>
    state.weeklyschedules.getIn(['data', shiftWeeklyscheduleId])
  )
  const labels = useMemoSelector((state: ReduxState) =>
    shiftWeeklyschedule
      ? getLabelsByTeamId(state, shiftWeeklyschedule.team_id)
      : Immutable.Map()
  )

  const shiftLabel =
    ((shift?.label_id &&
      labels.find(
        (label: { id: number; name: string; color: string }) =>
          shift.label_id === label?.id
      )) as {
      id: number
      name: string
      color: string
    }) || noLabel
  const labelColor = color(shiftLabel?.color).toString()

  const weeklyscheduleTeam = useMemoSelector((state: ReduxState) =>
    getTeamById(state, shiftWeeklyschedule.team_id)
  )

  const weeklyscheduleLocation = useMemoSelector(
    (state: ReduxState) =>
      weeklyscheduleTeam &&
      getLocationById(state, weeklyscheduleTeam.location_id)
  )

  const employeesCanSetRealHours =
    weeklyscheduleLocation &&
    weeklyscheduleLocation.employees_can_set_real_hours

  const { padType, hasSnapPadBreaksEnable, hasSnapPadMealEnable, breakAsReal } =
    usePadSettings()

  const shouldLockMeals = Boolean(account.lock_meals)
  const shiftIsLocked = Boolean(shift.locked_at)
  const breakArePaid = account.get('paid_breaks')

  const canValidate = useRealFormValidationPermissionLegacy({
    shiftData,
    precomputedCanValidate
  })

  const isManagerWhoCanValidateTheShift = atLeast.manager && canValidate

  const employeesCanModifyMeal =
    shouldLockMeals === false && atLeast.manager === false

  const onDeleteSubmit = () => {
    actions
      .deleteShift(shift?.id)
      .then(formSuccess)
      .catch(error => {
        setFormLoading(false)
        setFormError(error)
      })

    setFormLoading(true)
  }

  const getCommonShiftPayload = (formData: CommonFormRealDataLegacy) => {
    const { realStart, realEnd } = computeRealShiftDates(
      formData?.shift_time_range
        ? formData.shift_time_range
        : [shift.starts_at, shift.ends_at],

      [shift.starts_at, shift.ends_at]
    )

    const breaksToAdd = formData?.breaks
      ?.map(({ value: { plannedDuration, timeRange, id } }) => {
        const isEmptyTimeRange = !timeRange?.length || timeRange.includes('')
        const breakTime =
          !isEmptyTimeRange &&
          getFullDate({
            starts_at: realStart,
            time_range: timeRange
          })

        // We don't create break at 0
        if (plannedDuration === 0) {
          return
        }

        return {
          id: typeof id === 'string' ? undefined : id,
          real_duration: plannedDuration,
          ...(isEmptyTimeRange
            ? {
                real_starts_at: null,
                real_ends_at: null
              }
            : {
                // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
                real_starts_at: breakTime.momentStart.format(),
                // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
                real_ends_at: breakTime.momentEnd.format()
              })
        }
      })
      .filter(Boolean)

    const breaksToDelete = shift.shift_breaks
      ?.map(breakItem => {
        const isNewBreak = breaksToAdd?.some(
          newBreak => newBreak?.id === breakItem.get('id')
        )

        if (!isNewBreak && breakItem.get('id') !== undefined) {
          if (breakItem.get('planned_duration') !== 0) {
            return {
              id: breakItem.get('id'),
              real_duration: 0,
              real_starts_at: null,
              real_ends_at: null
            }
          }

          return { id: breakItem.get('id'), _destroy: 1 }
        }

        return null
      })
      .filter(Boolean)

    const shiftBreaks = breaksToAdd
      ? [...breaksToAdd, ...breaksToDelete]
      : breaksToDelete

    // we never delete meals in the real form we just update its real value to null
    // if meal hasn't been taken
    const shiftMeals = formData?.meals?.map(({ value: mealData }) => {
      // We're using initialMeal since the employee can't change his meal type
      const employeeMeal = mealData.checked ? mealData.initialMeal : null

      const managerMeal = mealData.checked ? mealData.meal.value : null

      /* This is to avoid any meal with null value since a user could create a meal
      when on the real hours form, validate the shift, unvalidate it then take the meal as non taken. */
      const shiftMeal = shift.shift_meals?.find(
        mealItem => mealItem.get('id') === mealData?.id
      )

      if (
        shiftMeal?.get('planned_meal_type') === null &&
        mealData.checked === false
      ) {
        return {
          id: mealData.id,
          _destroy: 1
        }
      }

      return {
        id: Number(mealData.id) ? mealData.id : null,
        real_meal_type: atLeast.manager ? managerMeal : employeeMeal
      }
    })

    const commonRealData = {
      id: shift.id,
      label_id:
        formData.label.value === NO_LABEL_VALUE || formData.label?.value === ''
          ? null
          : formData.label?.value || shift.label_id,
      real_starts_at: formData?.shift_time_range ? realStart : shift.starts_at,
      real_ends_at: formData?.shift_time_range ? realEnd : shift.ends_at,
      shift_breaks_attributes: shiftBreaks,
      shift_meals_attributes: shiftMeals,
      validator_id: canValidate ? currentUserId : null,
      validated_at: canValidate ? moment().format() : null,
      ...textFields.shift
    }

    return commonRealData
  }

  return {
    // Form State
    textFields,
    setTextField,
    formLoading,
    setFormLoading,
    formError,
    setFormError,

    // Form data
    shift,
    rest,
    contractLocationId,
    isManager: isManagerWhoCanValidateTheShift,
    isDirector: atLeast.director,
    employeesCanSetRealHours,
    labels,
    labelColor,
    defaultLocationMeal: weeklyscheduleLocation?.default_meal_type,
    padType,
    hasSnapPadBreaksEnable,
    hasSnapPadMealEnable,
    breakAsReal,
    shouldLockMeals,
    shiftIsLocked,
    breakArePaid,
    canValidate,
    employeesCanModifyMeal,

    // CTA, utils
    onDeleteSubmit,
    getCommonShiftPayload
  }
}
